import { Flex, HStack, Tag, TagLabel, Text } from '@chakra-ui/react';
import React from 'react';
import { CustomFieldValue } from '../../../types';

type ClosestUrgentCareBadgeProps = {
  customField?: CustomFieldValue[];
};

// Linked clinics are stored as { practiceId, practiceLabel }, but older
// records use the generic { id, label } shape - accept both.
type StoredPractice = {
  practiceId?: string;
  id?: string;
  practiceLabel?: string;
  label?: string;
};

const normalizePractices = (
  stored: StoredPractice[] | undefined
): { practiceId: string; practiceLabel: string }[] => {
  if (!Array.isArray(stored)) return [];
  return stored
    .map((practice) => ({
      practiceId: practice.practiceId || practice.id || '',
      practiceLabel: practice.practiceLabel || practice.label || '',
    }))
    .filter((practice) => practice.practiceId);
};

const ClosestUrgentCareBadge: React.FC<
  ClosestUrgentCareBadgeProps
> = ({ customField }) => {
  if (!Array.isArray(customField) || customField.length === 0) {
    return null;
  }

  const field =
    customField.find(
      (r) => typeof r.value === 'string' && r.value.trim() !== ''
    ) || customField[0];
  const practices = normalizePractices(
    field.metadata?.practices as StoredPractice[] | undefined
  );
  const freeTextInstructions =
    typeof field.metadata?.freeTextInstructions === 'string'
      ? field.metadata.freeTextInstructions.trim()
      : field.metadata?.freeTextInstructions;
  const value = typeof field.value === 'string' ? field.value.trim() : '';

  if (!value && practices.length === 0 && !freeTextInstructions) {
    return null;
  }

  // With no linked clinic the free text is already shown as the main value,
  // so only repeat it when it is not what is being displayed above.
  const showFreeText =
    Boolean(freeTextInstructions) &&
    (practices.length > 0 || String(freeTextInstructions) !== value);

  return (
    <Flex direction="column" gap="0.25rem" align="flex-end">
      {practices.length > 0 ? (
        <HStack spacing={1} flexWrap="wrap" justify="flex-end">
          {practices.map((p) => (
            <Tag
              as="a"
              key={p.practiceId}
              size="sm"
              colorScheme="blue"
              borderRadius="full"
              variant="outline"
              cursor="pointer"
              href={`/practices/${p.practiceId}`}
              target="_blank"
              rel="noopener noreferrer"
              _hover={{
                bg: 'blue.50',
                borderColor: 'blue.400',
                transform: 'translateY(-1px)',
              }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.15s ease-in-out"
              fontSize="0.75rem"
              fontWeight="500"
            >
              <TagLabel>{p.practiceLabel || value || p.practiceId}</TagLabel>
            </Tag>
          ))}
        </HStack>
      ) : (
        <Text
          fontSize="0.875rem"
          fontWeight="700"
          color="text.primary"
          lineHeight="1.25rem"
        >
          {value}
        </Text>
      )}

      {showFreeText && (
        <Text fontSize="0.75rem" color="gray.600" fontStyle="italic">
          {String(freeTextInstructions)}
        </Text>
      )}
    </Flex>
  );
};

export default ClosestUrgentCareBadge;
